import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridRow } from '@naf/grid';
import { UserInteractionVariant } from '@naf/user-interaction';
import { toCurrentSiteUrl } from '@lib/siteHosts';
import { SearchInformationBox as SearchInformationBoxType } from '../../../hooks/GlobalSearchTypes';
import BlockContent from '../../block-content/BlockContent';
import { useDocumentUrlWithNode } from '../../../hooks/Documents/useDocumentUrl';
import { useTrackSearchResultClick } from '../../../hooks/useTrackSearchResultClick';
import * as S from './SearchInformationBox.styles';

interface SearchInformationBoxProps {
  searchInformationBox: SearchInformationBoxType;
  searchInputText?: string;
  onCloseSearch: () => void;
  isInModal?: boolean;
}

export const SearchInformationBox = ({
  searchInformationBox,
  searchInputText,
  onCloseSearch,
  isInModal = false,
}: SearchInformationBoxProps) => {
  const navigate = useNavigate();
  const { ctaButton, content, variant } = searchInformationBox;
  const ctaUrl = useDocumentUrlWithNode(ctaButton?.ctaInternalLink?.referenceComponent);
  const { trackClickSearchResult } = useTrackSearchResultClick(true);

  const buttonProps = useMemo(() => {
    if (!ctaButton) return undefined;

    const isInternalLink = ctaButton.type === 'InternalLink' && ctaUrl;
    const isExternalLink = ctaButton.type === 'ExternalUrl' && ctaButton.url;

    if (!isInternalLink && !isExternalLink) return undefined;

    return {
      onClick: () => {
        trackClickSearchResult();
        onCloseSearch();

        if (isInternalLink) {
          navigate(ctaUrl);
        } else if (isExternalLink && ctaButton.url) {
          if (ctaButton.blank) {
            window.open(toCurrentSiteUrl(ctaButton.url), '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = toCurrentSiteUrl(ctaButton.url);
          }
        }
      },
      children: ctaButton.buttonText,
      type: 'button' as const,
    };
  }, [ctaButton, ctaUrl, navigate, onCloseSearch, trackClickSearchResult]);

  const infoboxVariant = variant === 'information' ? 'info' : variant;

  return (
    <GridRow>
      <S.ResultGridCol s="12" m="12" l={isInModal ? '12' : '10'} xl={isInModal ? '12' : '6'} $isInModal={isInModal}>
        <S.StyledUserInteraction
          variant={infoboxVariant as UserInteractionVariant}
          title={`
          Du har søkt etter "${searchInputText}"`}
          button={buttonProps}
        >
          <div>
            <BlockContent value={content} />
          </div>
        </S.StyledUserInteraction>
      </S.ResultGridCol>
    </GridRow>
  );
};
